import React, { useEffect, useRef } from 'react';
import {
    Dropdown,
    PreviewPanel,
} from '@instinet-analytics/semantic-ui-components';
import {
    DropdownItemProps,
    Icon,
} from '@instinet-analytics/semantic-ui-components';
import './WidgetPreview.scss';
import { useWidgetLayouts } from 'layoutManagement/useWidgetLayouts';
import ManagedLayout from 'layoutManagement/ManagedLayout';
import { AddWidgetSelectedItem } from 'appToolbar/AddWidgetModal/AddWidgetModal';
import { useAddLayoutDragSourcePubSub } from '../AddLayoutDragSourcePubSub';
import { AnyConfig } from '../../widgets/WidgetRegistry';
import { useServices } from '../../app/hooks';
import { WidgetType } from '../../widgets/WidgetType';
import classNames from 'classnames';

interface WidgetPreviewProps {
    addMultiple: boolean;
    previewItem?: AddWidgetSelectedItem;
    onSelectedLayoutChanged: (l?: ManagedLayout) => void;
}

export function WidgetPreview(props: WidgetPreviewProps) {
    const { addMultiple, onSelectedLayoutChanged, previewItem } = props;

    const selectedWidget = previewItem?.widget;

    const layouts = useWidgetLayouts(selectedWidget?.type);

    return (
        <div className="WidgetPreview">
            {selectedWidget && (
                <div className="widget-preview">
                    {selectedWidget.description ? (
                        <>
                            <h1>{selectedWidget.title}</h1>
                            {selectedWidget.description!.body}
                            <PreviewPanel
                                previews={selectedWidget.description!.previews}
                                minPreviewHeight={200}
                            />
                        </>
                    ) : (
                        <div>{selectedWidget.title}</div>
                    )}
                </div>
            )}
            {!previewItem && (
                <div className="no-selection">
                    <div>
                        {!addMultiple && (
                            <>
                                <div>
                                    Click a row to view an overview and preview
                                    of the widget.
                                </div>
                                <div>
                                    Drag and drop a row onto the required
                                    position in your layout.
                                </div>
                                <div>
                                    Double click a row, or click the{' '}
                                    <Icon name="plus circle" />
                                    button, to add the widget to your layout.
                                </div>
                                <div>
                                    Check &apos;Add Multiple&apos; to add more
                                    than one widget.
                                </div>
                            </>
                        )}
                        {addMultiple && (
                            <>
                                <div>
                                    Click rows to select the widgets to be
                                    added.
                                </div>
                                <div>
                                    Uncheck &apos;Add Multiple&apos; to switch
                                    back to single select mode.
                                </div>
                            </>
                        )}
                    </div>
                </div>
            )}
            <div className="bottom">
                {layouts.length > 0 && (
                    <div className="select-layout">
                        <SelectLayoutDropdown
                            layouts={layouts}
                            selectedLayout={previewItem?.layout}
                            onSelectedLayoutChanged={onSelectedLayoutChanged}
                        />
                    </div>
                )}

                {previewItem && (
                    <>
                        <DragPreviewBox
                            layout={undefined}
                            widgetType={selectedWidget!.type}
                            widgetTitle={selectedWidget!.title}
                            visible={!previewItem.layout}
                        />
                        {layouts.map((l) => (
                            <DragPreviewBox
                                key={l.layoutId + l.isGlobal.toString()}
                                layout={l}
                                widgetType={selectedWidget!.type}
                                widgetTitle={selectedWidget!.title}
                                visible={
                                    previewItem?.layout?.name === l.name &&
                                    !!previewItem?.layout?.isGlobal ===
                                        l.isGlobal
                                }
                            />
                        ))}
                    </>
                )}
            </div>
        </div>
    );
}

function DragPreviewBox(props: {
    layout: ManagedLayout | undefined;
    widgetType: WidgetType;
    widgetTitle: string;
    visible: boolean;
}) {
    const { layout, widgetType, widgetTitle, visible } = props;
    const { layoutService } = useServices();
    const dragSourceRef = useRef<HTMLDivElement>(null);
    const addLayoutDragSourcePubSub = useAddLayoutDragSourcePubSub();

    useEffect(() => {
        if (!dragSourceRef.current) {
            return;
        }

        (async () => {
            let config: AnyConfig | undefined = undefined;
            if (layout) {
                const dto = await layoutService.fetchLayout(
                    layout.name,
                    widgetType,
                    layout.isGlobal
                );
                config = JSON.parse(dto.layout);
            }

            addLayoutDragSourcePubSub.publishAdd({
                type: widgetType,
                title: widgetTitle,
                element: dragSourceRef.current!,
                config,
                uid: `WidgetPreview-${widgetType}-${layout?.name ?? 'default'}`,
            });
        })();
    }, [
        addLayoutDragSourcePubSub,
        widgetType,
        widgetTitle,
        layout,
        layoutService,
    ]);

    const cssClasses = classNames(
        'DragPreviewBox drag',
        visible ? undefined : 'hidden'
    );

    return (
        <div className={cssClasses} ref={dragSourceRef}>
            <div className="InfoBox">
                Drag into position <Icon name="grab" className="drag-button" />
            </div>
        </div>
    );
}

function SelectLayoutDropdown(props: {
    layouts: ManagedLayout[];
    selectedLayout?: ManagedLayout;
    onSelectedLayoutChanged: (l?: ManagedLayout) => void;
}) {
    const DefaultId = 'Default';
    const toLayoutId = (l: ManagedLayout) =>
        l.isGlobal ? `global-${l.name}` : l.layoutId.toString();

    const { layouts, selectedLayout, onSelectedLayoutChanged } = props;

    const selectedId = selectedLayout ? toLayoutId(selectedLayout) : DefaultId;

    const options: DropdownItemProps[] = [
        {
            value: DefaultId,
            text: 'Default',
        },
        ...layouts.map((l) => ({
            value: toLayoutId(l),
            text: `${l.name}${l.isGlobal ? ' (Global)' : ''}`,
        })),
    ];

    const onChange = (id: string) => {
        const nextLayout =
            id === DefaultId
                ? undefined
                : layouts.find((l) => toLayoutId(l) === id);
        onSelectedLayoutChanged(nextLayout);
    };

    return (
        <div className="select-layout-dropdown">
            <span>Layout:</span>
            <Dropdown
                options={options}
                value={selectedId}
                onChange={onChange}
                bottom={true}
            />
        </div>
    );
}
